import PropTypes from 'prop-types';

import { getContent, getContentType, getFields, isDDEnv } from '@/utils/helper';

import AnchorNavigationScroll from '@/molecules/AnchorNavigation/AnchorNavigationScroll';
import BlockText from '@/molecules/BlockText/BlockText';
import Carousel from '@/organisms/Carousel/Carousel';
import Card from '@/molecules/Card/Card';
import ContentFooter from '@/organisms/ContentFooter/ContentFooter';
import ContactForm from '@/organisms/ContactForm/ContactForm';
import ContactItem, {
  ContactDescription,
} from '@/molecules/ContactItem/ContactItem';
import ProductTile from '@/molecules/ProductTile/ProductTile';
import ChefIntroduction from '@/organisms/ChefIntroduction/ChefIntroduction';
import ContentGrid from '@/organisms/ContentGrid/ContentGrid';
import Cut from '@/organisms/Cut/Cut';
import CutsHeader from '@/organisms/CutsHeader';
import DynamicGrid from '@/organisms/DynamicGrid/DynamicGrid';
import DynamicsForm from '@/organisms/DynamicsForm/WithOneTrustCookie';
import DDIngredientSection from '@/organisms/IngredientSection/DDIngredientSection';
import Faq from '@/organisms/Faq/Faq';
import Filters from '@/organisms/Filters/Filters';
import FloatingAsset from '@/molecules/FloatingAsset/FloatingAsset';
import Footer from '@/organisms/Footer/Footer';
import Header from '@/organisms/Header/Header';
import HeaderHome from '@/organisms/Header/HeaderHome';
import HtmlStringEmbed from '@/molecules/HtmlStringEmbed/HtmlStringEmbed';
import IngredientSection from '@/organisms/IngredientSection/IngredientSection';
import LeadGeneration from '@/organisms/LeadGeneration/LeadGeneration';
import Map from '@/organisms/Map/Map';
import Navigation from '@/organisms/Navigation/Navigation';
import RecipeHeader from '@/organisms/RecipeHeader/RecipeHeader';
import RecipeOverview from '@/organisms/RecipeOverview/RecipeOverview';
import SectionHeader from '@/molecules/SectionHeader/SectionHeader';
import Sliders from '@/organisms/Sliders/Sliders';
import SocialAggregator from '@/organisms/SocialAggregator/SocialAggregator';
import StoryF from '@/organisms/Story/Story';
import StoryProduct from '@/organisms/StoryProduct/StoryProduct';
import TwoColumnContent, {
  ContentColumn,
} from '@/organisms/TwoColumnContent/TwoColumnContent';
import TwoColumnBlockText from '@/organisms/TwoColumnBlockText/TwoColumnBlockText';
import ProductsKind from '@/organisms/ProductsKind/ProductsKind';
import StaticContent from '@/organisms/StaticContent/StaticContent';
import ColumnsTextBlock from '@/organisms/ColumnsTextBlock/ColumnsTextBlock';
import MapWestholme from '@/organisms/Map/MapWestholme';
import RecipeHeaderDD from '@/organisms/RecipeHeader/RecipeHeaderDD';
import ProductDetail from '@/organisms/ProductDetail/ProductDetail';

function BlockRenderer({
  assets = [],
  anchorNavigation,
  blocks = [],
  entries = [],
  tags = [],
  slug,
  showVersion,
  columnLayout,
  compactSpace, // treat :md like :sm (remove additional padding)
  compactText,
  carouselContentStyle = {}, // sample usage in stationdetailsidebar
  pageTitle,
  richTextStyle = {}, // sample usage in stationdetailsidebar
  isFromTwoColumnContent = false,
  isFromContentColumn = false,
  hasBackground,
  navigationColor,
}) {
  const isDD = isDDEnv();
  return (
    <>
      {blocks.map((contentItem, index) => {
        const content = getContent(contentItem.sys.id, entries);
        if (!content) return null;
        const contentType = getContentType(content);
        const fields = getFields(content);

        let renderedBlock = null;

        switch (contentType) {
          case 'anchorNavigation':
            renderedBlock = (
              <AnchorNavigationScroll
                anchor={{ fields, sys: { id: contentItem.sys.id } }}
                assets={assets}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'carousel':
            renderedBlock = (
              <Carousel
                assets={assets}
                carouselContentStyle={carouselContentStyle}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'contentColumn':
            renderedBlock = (
              <ContentColumn
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
                columnLayout={columnLayout}
                isBlock={true}
                position="content"
                isFromTwoColumnContent={isFromTwoColumnContent}
                isFromContentColumn={isFromContentColumn}
              />
            );
            break;
          case 'contentGrid':
            if (fields.queryTags) {
              renderedBlock = (
                <DynamicGrid
                  assets={assets}
                  fields={fields}
                  entries={entries}
                  key={contentItem.sys.id + index}
                  columnLayout={columnLayout}
                  isFromTwoColumnContent={isFromTwoColumnContent}
                  isFromContentColumn={isFromContentColumn}
                  hasBackground={hasBackground}
                />
              );
            } else {
              renderedBlock = (
                <ContentGrid
                  assets={assets}
                  fields={fields}
                  entries={entries}
                  key={contentItem.sys.id + index}
                  columnLayout={columnLayout}
                  isFromTwoColumnContent={isFromTwoColumnContent}
                  isFromContentColumn={isFromContentColumn}
                  hasBackground={hasBackground}
                />
              );
            }
            break;
          case 'card':
            renderedBlock = (
              <Card
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
                isBlock={true}
                isFromContentColumn={isFromContentColumn}
              />
            );
            break;
          case 'cutsHeader':
            renderedBlock = (
              <CutsHeader
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
                slug={slug}
              />
            );
            break;
          case 'cut':
            renderedBlock = (
              <Cut
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'header':
            renderedBlock = (
              <Header
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
                slug={slug}
              />
            );
            break;
          case 'headerHome': {
            renderedBlock = (
              <HeaderHome
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
                slug={slug}
              />
            );
            break;
          }
          case 'navigation':
            renderedBlock = (
              <Navigation
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
                tags={tags}
                slug={slug}
                isBlock={true}
                navigationColor={navigationColor}
              />
            );
            break;
          case 'contentFooter':
            renderedBlock = (
              <ContentFooter
                assets={assets}
                fields={fields}
                entries={entries}
                tags={tags}
                pageTitle={pageTitle}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'footer':
            renderedBlock = (
              <Footer
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
                showVersion={showVersion}
              />
            );
            break;
          case 'sliders':
            renderedBlock = (
              <Sliders
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'story':
            renderedBlock = (
              <StoryF
                assets={assets}
                compactSpace={compactSpace}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'storyProduct':
            renderedBlock = (
              <StoryProduct
                assets={assets}
                compactSpace={compactSpace}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'chefIntroduction':
            renderedBlock = (
              <ChefIntroduction
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'filters':
            renderedBlock = (
              <Filters
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'sectionHeader':
            renderedBlock = (
              <SectionHeader
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
                columnLayout={columnLayout}
                compactText={compactText}
                hasBackground={hasBackground}
              />
            );
            break;
          case 'ddIngredientSection': {
            renderedBlock = (
              <DDIngredientSection
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          }
          case 'ingredientSection': {
            renderedBlock = (
              <IngredientSection
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          }
          case 'recipeHeader': {
            if (isDD) {
              return (renderedBlock = (
                <RecipeHeaderDD
                  assets={assets}
                  fields={fields}
                  entries={entries}
                  key={contentItem.sys.id + index}
                  slug={slug}
                  anchorNavigation={anchorNavigation}
                />
              ));
            }
            renderedBlock = (
              <RecipeHeader
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
                slug={slug}
                anchorNavigation={anchorNavigation}
              />
            );
            break;
          }
          case 'recipeOverview': {
            renderedBlock = (
              <RecipeOverview
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
                slug={slug}
              />
            );
            break;
          }
          case 'blockText':
            renderedBlock = (
              <BlockText
                assets={assets}
                compactSpace={compactSpace}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
                richTextStyle={richTextStyle}
              />
            );
            break;
          case 'floatingAsset':
            renderedBlock = (
              <FloatingAsset
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          // case 'productTile':
          //   renderedBlock = (
          //     <ProductTile
          //       assets={assets}
          //       fields={fields}
          //       entries={entries}
          //       key={contentItem.sys.id + index}
          //     />
          //   );
          //   break;
          case 'twoColumnContent':
            renderedBlock = (
              <TwoColumnContent
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          // case 'twoColumnBlockText':
          //   renderedBlock = (
          //     <TwoColumnBlockText
          //       fields={fields}
          //       entries={entries}
          //       key={contentItem.sys.id + index}
          //     />
          //   );
          //   break;
          case 'contactForm':
            renderedBlock = (
              <ContactForm
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'map':
            renderedBlock = (
              <MapWestholme
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'faq':
            renderedBlock = (
              <Faq
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'contactItem':
            renderedBlock = (
              <ContactItem
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'contactDescription':
            renderedBlock = (
              <ContactDescription
                fields={fields}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'leadGeneration':
            renderedBlock = (
              <LeadGeneration
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'socialAggregator':
            renderedBlock = (
              <SocialAggregator
                assets={assets}
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'productKind':
            renderedBlock = (
              <ProductsKind
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'staticContent':
            renderedBlock = (
              <StaticContent
                assets={assets}
                entries={entries}
                fields={fields}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'htmlStringEmbed':
            renderedBlock = (
              <DynamicsForm
                key={contentItem.sys.id + index}
                content={fields?.htmlContent}
                name={fields?.name}
                type={fields?.type}
              />
            );
            break;
          case 'twoColumnBlockText':
            renderedBlock = (
              <ColumnsTextBlock
                entries={entries}
                fields={fields}
                key={contentItem.sys.id + index}
              />
            );
            break;
          case 'productDetail':
            renderedBlock = (
              <ProductDetail
                fields={fields}
                entries={entries}
                key={contentItem.sys.id + index}
              />
            );
            break;
        }
        return renderedBlock;
      })}
    </>
  );
}

// @TODO: define real props for the Block Renderer based on it requirements
BlockRenderer.propTypes = {
  assets: PropTypes.any,
  blocks: PropTypes.any,
  compactSpace: PropTypes.bool,
  carouselContentStyle: PropTypes.object,
  richTextStyle: PropTypes.object,
  entries: PropTypes.any,
  slug: PropTypes.string,
  pageTitle: PropTypes.string,
};

BlockRenderer.defaultProps = {
  compactSpace: false,
  carouselContentStyle: {
    compactSpace: false,
    hasBackground: false,
    linkStyle: '',
  },
  richTextStyle: {},
};

export default BlockRenderer;
